import { LODGE_EMAIL, LODGE_EMAIL_HREF, LODGE_PHONE_DISPLAY, LODGE_PHONE_HREF, callAriaLabel } from "@/lib/contact-info";
import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/terms")({
  head: () =>
    seoHead({
      path: "/terms",
      title: "Terms of Service — Coffman Cove Lodging",
      description:
        "Terms for booking lodging, meals, and boat rentals with Coffman Cove Lodging on Prince of Wales Island, Alaska.",
      extraMeta: [{ name: "robots", content: "index,follow" }],
    }),
  component: TermsPage,
});

const lastUpdated = "June 30, 2026";

function TermsPage() {
  return (
    <article className="mx-auto max-w-3xl px-4 sm:px-6 py-16 sm:py-20 text-[var(--offwhite)]">
      <p className="text-xs uppercase tracking-[0.22em] text-[var(--electric)]">Legal</p>
      <h1 className="mt-2 font-display text-4xl md:text-5xl text-white">Terms of Service</h1>
      <p className="mt-3 text-sm text-[var(--offwhite-warm)]/70">Last updated {lastUpdated}</p>

      <p className="mt-8 rounded-xl border border-[var(--electric)]/20 bg-[var(--electric)]/5 p-4 text-sm leading-relaxed text-[var(--offwhite-warm)]/85">
        These terms describe how Coffman Cove Lodging ("we," "us") provides lodging, meals,
        and boat rentals, and how you ("guest") use our website and services. By booking with
        us or using this site, you agree to these terms.
      </p>

      <Section title="Reservations and payment">
        A deposit is required to hold your dates. The balance is due on arrival unless we
        agree otherwise in writing. Rates, taxes, and fees in effect at the time of booking
        apply to your stay.
      </Section>

      <Section title="Cancellations and changes">
        Because our season is short and bookings fill far in advance, cancellation terms are
        provided in your booking confirmation. Date changes are handled on a best-effort
        basis subject to availability.
      </Section>

      <Section title="Boat rentals and fishing">
        Boat rentals require a signed rental agreement, a safety briefing, and a valid
        Alaska sport-fishing license per angler. Guests are responsible for following all
        State of Alaska Department of Fish & Game regulations, posted bag and possession
        limits, and Coast Guard safety rules. We may pause or cancel rentals for weather,
        sea state, or mechanical reasons.
      </Section>

      <Section title="Property and damages">
        Please treat the lodge, cabins, boats, and equipment with care. Guests are
        responsible for loss or damage beyond normal wear, including fuel returned below the
        agreed level.
      </Section>

      <Section title="Assumption of risk">
        Outdoor activities in Southeast Alaska — boating, fishing, hiking, wildlife viewing
        — involve inherent risks including weather, water, and remote conditions. By
        participating, you accept these risks and agree to follow staff instructions and
        posted safety guidance.
      </Section>

      <Section title="Fishing report and weather data">
        Tides, weather, marine conditions, and fishing suggestions on this site are pulled
        from public data sources (NOAA, Open-Meteo, USNO) and are provided as a courtesy.
        Conditions can change quickly — always check official sources and use your own
        judgment before going on the water.
      </Section>

      <Section title="Website content">
        Photography, video, copy, and the design of this website are the property of Coffman
        Cove Lodging or used with permission. Please don't reuse them commercially without
        asking.
      </Section>

      <Section title="Changes to these terms">
        We may update these terms occasionally. The "Last updated" date at the top reflects
        the most recent version.
      </Section>

      <Section title="Contact">
        Questions about these terms? Email{" "}
        <a className="text-[var(--electric)] hover:underline" href={LODGE_EMAIL_HREF}>
          {LODGE_EMAIL}
        </a>{" "}
        or call{" "}
        <a
          className="inline-flex min-h-11 items-center text-[var(--electric)] hover:underline"
          href={LODGE_PHONE_HREF}
          aria-label={callAriaLabel(LODGE_PHONE_DISPLAY)}
        >
          {LODGE_PHONE_DISPLAY}
        </a>.

      </Section>

      <div className="mt-12 border-t border-[var(--electric)]/15 pt-6 text-sm text-[var(--offwhite-warm)]/80">
        See also our <Link to="/privacy" className="text-[var(--electric)] hover:underline">Privacy Policy</Link>.
      </div>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-2xl text-[var(--offwhite)]">{title}</h2>
      <p className="mt-3 text-[15px] leading-relaxed text-[var(--offwhite-warm)]/85">{children}</p>
    </section>
  );
}
